import { ABLEITUNGS_RESERVE, NAME_BUDGET, setzeNamensBudget } from '../domain/dateiname';

import { appendAppLog } from './appLog';
import { loescheOrdner, schreibeText } from './dateien';
import { desktopBruecke } from './gleis';
import { buildFileName } from './storage';

/**
 * Passt der längste Pfad, den Stage anlegt, auf dieses System? (Auftrag des Nutzers, 2026-10-22)
 *
 * ⚠️ **Windows zählt den ganzen Pfad, nicht den Namen.** 260 Zeichen samt Laufwerk und
 * Abschlussnull. Node schreibt zwar darüber hinaus, aber Explorer, OneDrive, das Kopieren auf
 * einen Stick und fremde Entpacker scheitern — und genau dorthin sollen die Belege. Unter
 * `C:\Users\x\Documents\fairexa Stage` kam der volle Name im Archiv auf rund 300 Zeichen.
 * Deshalb wird dort das Namensbudget an die Wurzel angepasst; es fallen dann Felder weg
 * (`storage.ts::kuerzeAuf`, Schauspieler zuerst), die ohnehin in der Datenbank stehen.
 *
 * Nur am Desktop. Am iPad sind es 1024 Zeichen bei einer Wurzel von rund 80 — dort greift
 * allein die Namensgrenze, und die hält `domain/dateiname.ts` schon.
 *
 * ⛔ **Hält den Start nicht auf und wirft nie.** Ein Treffer steht im Log und als Einblendung.
 */

/** Der tiefste Ordner unter der Wurzel: Archivtag, Bezug, Art (`archive.ts`, `storage.ts::logFolder`). */
const TIEFSTER_ORDNER = 'old/0000-00-00/logs/station-comment/handwriting/';

/** `MAX_PATH` 260 weniger der Abschlussnull. */
export const WINDOWS_GRENZE = 259;

/** Was der Grundname auf diesem System höchstens haben darf. */
export function budgetFuer(plattform: string, wurzel: string): number {
  if (plattform !== 'win32') return NAME_BUDGET;
  // +1 für den Trenner zwischen Wurzel und Ordner.
  return Math.min(NAME_BUDGET, WINDOWS_GRENZE - wurzel.length - 1 - TIEFSTER_ORDNER.length - ABLEITUNGS_RESERVE);
}

/** Was von einem Namen bleiben muss, wenn alle weglassbaren Felder gefallen sind. */
export function pflichtLaenge(): number {
  const uuid = '00000000-0000-4000-8000-000000000000';
  return buildFileName('handwriting', { examId: uuid, candidateId: uuid, examinerId: '' }, 'jpeg').length;
}

export async function pruefePfadlaenge(zeigeTreffer: () => void): Promise<void> {
  try {
    const bruecke = desktopBruecke();
    if (!bruecke) return;
    const wurzel = await bruecke.dateien.wurzel();
    const budget = budgetFuer(bruecke.plattform, wurzel);
    setzeNamensBudget(budget);

    const pflicht = pflichtLaenge();
    let probe = 'nicht versucht';
    if (budget >= pflicht) {
      // Ein Ordner so tief wie der tiefste, darin ein Name so lang wie erlaubt samt Anhängsel.
      const ordner = 'tmp/' + 'p'.repeat(TIEFSTER_ORDNER.length - 'tmp/'.length - 1);
      try {
        await schreibeText(ordner + '/' + 'x'.repeat(budget + ABLEITUNGS_RESERVE), '', { ordnerAnlegen: true });
        probe = 'ok';
      } catch (fehler) {
        probe = 'gescheitert: ' + String(fehler);
      }
      await loescheOrdner(ordner).catch(() => undefined);
    }

    const zeile =
      'Pfadlänge: Wurzel ' + String(wurzel.length) + ' Zeichen (' + wurzel + '), Namensbudget ' +
      String(budget) + ' von ' + String(NAME_BUDGET) + ', Pflichtteil ' + String(pflicht) + ', Probe ' + probe;
    const treffer = budget < pflicht || probe !== 'ok';
    await appendAppLog(zeile, treffer ? 'warn' : 'info').catch(() => undefined);
    if (treffer) zeigeTreffer();
  } catch (fehler) {
    await appendAppLog('Pfadlänge nicht geprüft: ' + String(fehler), 'warn').catch(() => undefined);
  }
}
